/**
 * The Information Point types, grouped for display, in display order. Groups exist only here:
 * the stored hierarchy is root → leaf, and the leaves it holds under `information_points` are
 * exactly these kinds in this order (`LocationTypeHierarchy.BuildOptions()`).
 *
 * Labels live in the localization trees under `informationPointTypes` and
 * `informationPointGroups`, keyed by these slugs.
 */
export const INFORMATION_POINT_GROUPS = [
  { id: 'general', kinds: ['general_information_point'] },
  {
    id: 'entry_restrictions',
    kinds: [
      'access',
      'exit',
      'restricted_area',
      'security_checkpoint',
      'ppe_zone',
      'controlled_environment',
      'site_rules',
    ],
  },
  {
    id: 'hazards',
    kinds: ['hazard_zone', 'confined_space', 'energized_equipment'],
  },
  {
    id: 'site_logistics',
    kinds: [
      'walkway',
      'parking',
      'loading',
      'traffic_control',
      'waste',
      'stormwater_erosion_control',
      'elevated_access',
    ],
  },
  {
    id: 'offices_utilities',
    kinds: ['site_office', 'trailer', 'electrical_room', 'utilities'],
  },
  {
    id: 'restrooms_breaks',
    kinds: [
      'restrooms',
      'break_area',
      'canteen',
      'hydration_station',
      'handwash_station',
      'smoking_area',
    ],
  },
  {
    id: 'emergency',
    kinds: ['medical_area', 'fire_protection', 'muster_point'],
  },
] as const

export type InformationPointGroupId =
  (typeof INFORMATION_POINT_GROUPS)[number]['id']

export type InformationPointKind =
  (typeof INFORMATION_POINT_GROUPS)[number]['kinds'][number]

export const INFORMATION_POINT_KINDS: readonly InformationPointKind[] =
  INFORMATION_POINT_GROUPS.flatMap(group => group.kinds)

const GROUP_BY_KIND: Readonly<Record<string, InformationPointGroupId>> =
  Object.fromEntries(
    INFORMATION_POINT_GROUPS.flatMap(group =>
      group.kinds.map(kind => [kind, group.id]),
    ),
  )

export const isInformationPointKind = (
  kind: string,
): kind is InformationPointKind => kind in GROUP_BY_KIND

export const informationPointGroupOf = (
  kind: InformationPointKind,
): InformationPointGroupId => GROUP_BY_KIND[kind]
